/**
 * Permanent reassurance banner for a driver whose vehicle is currently private.
 *
 * The point is not to warn — nothing is wrong — but to make the protection
 * visible while it applies. A driver who cannot see that privacy is on has to
 * take the company's word for it, which is exactly the trust problem this
 * module exists to solve.
 *
 * Self-gating like OfflineBanner/InstallBanner: renders nothing unless the
 * signed-in user actually has a vehicle in a privacy window right now.
 */

import { useQuery } from '@tanstack/react-query';
import { Shield } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { privacyApi } from '../services/api';

/**
 * A privacy window can end at any minute, and the flag is computed server-side
 * from the position timestamp, so the banner polls rather than relying on a
 * mount-time read.
 */
const REFRESH_MS = 60_000;

export default function PrivacyBanner() {
  const { data: devices } = useQuery({
    queryKey: ['my-privacy'],
    queryFn: privacyApi.getMine,
    refetchInterval: REFRESH_MS,
    // A user with no assigned vehicle gets an empty array; nothing renders.
    retry: false,
  });

  const active = devices?.filter((device) => device.activeNow) ?? [];
  if (active.length === 0) return null;

  // The end of the running declared period, when there is one. A vehicle
  // private because of the recurring nightly window has no period and no end
  // date to show — saying "until 08:00 tomorrow" there would be a guess.
  const runningPeriods = active
    .flatMap((device) => device.periods)
    .filter((period) => {
      const now = Date.now();
      return new Date(period.startsAt).getTime() <= now && new Date(period.endsAt).getTime() > now;
    })
    .sort((a, b) => new Date(b.endsAt).getTime() - new Date(a.endsAt).getTime());

  const until = runningPeriods[0]
    ? format(new Date(runningPeriods[0].endsAt), 'd MMMM', { locale: fr })
    : null;

  const subject =
    active.length === 1 ? active[0].name : `${active.length} de vos véhicules`;

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-indigo-600 text-white px-4 py-2"
    >
      <div className="flex items-center gap-2">
        <Shield className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm font-medium">
          Mode privé actif — aucune position n'est enregistrée pour {subject}
          {until ? ` jusqu'au ${until}` : ''}.
        </span>
      </div>
    </div>
  );
}
